import React, { useState, useEffect } from 'react';
import {
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Menu,
    MenuItem
} from '@mui/material';
import { Add, MoreVert } from '@mui/icons-material';
import Spinner from "../../components/Spinner";
import { makeStyles } from '@mui/styles';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createFAQ, listFAQs, editFAQ, deleteFAQ } from "../../http/auth";
import CommonPagination from '../../components/Table/CommonPagination';
import ExpandableText from '../../components/ExpandableText';
import CustomTextField from '../../components/FormElements/CustomTextField';
import { useDispatch } from 'react-redux';
import { showToast } from '../../store/slices/common';
import ConfirmDeleteModal from '../ManageBadge/ConfirmDeleteModal';
import PageLayout from "../../components/PageLayout";

const useStyles = makeStyles((theme) => ({
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px',
    }, dialogContent: {
        width: '500px', minWidth: '500px', [theme.breakpoints.down('xs')]: {
            width: '100%', minWidth: '100%',
        },
    }, input: {
        '& label.Mui-focused': {
            color: '#142C5C',
        }, '& .MuiInput-underline:after': {
            borderBottomColor: '#142C5C',
        }, '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#142C5C',
            }, '&:hover fieldset': {
                borderColor: '#142C5C',
            }, '&.Mui-focused fieldset': {
                borderColor: '#142C5C',
            },
        },
    }, button: {
        backgroundColor: '#142C5C', color: '#fff', '&:hover': {
            backgroundColor: '#0f2247',
        },
    }, tableContainer: {
        maxHeight: 'calc(100vh - 150px)',
        minHeight: 'calc(100vh - 150px)',
        overflowX: 'auto', overflowY: 'auto', boxShadow: 'none !important',
    }, table: {
        minWidth: '100 %', border: '1px solid rgba(224, 224, 224, 1)'
    }, tableHead: {
        position: 'sticky', top: '-1px', backgroundColor: '#fff', zIndex: 1,
    },
}));

const QueryManager = () => {
    const classes = useStyles();
    const [queries, setQueries] = useState([]);
    const [queryName, setQueryName] = useState('');
    const [queryDescription, setQueryDescription] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [viewQuestion, setViewQuestion] = useState('');
    const [viewDescription, setViewDescription] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [totalFaq, setTotalfaq] = useState(0)
    const dispatch = useDispatch()
    const limit = 15

    useEffect(() => {
        fetchFAQs();
    }, [page, limit]);

    const fetchFAQs = async () => {
        setLoading(true);
        try {
            const response = await listFAQs({ page, limit });
            setTotalfaq(response?.data?.resultCount)
            setQueries(response.data.list);
            console.log(response.data.list)
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setQueryName('');
        setQueryDescription('');
        setIsEditMode(false);
        setEditIndex(null);
        setOpenDialog(false);
    };

    const validate = (queryName, queryDescription) => {
        if (!queryName.trim()) {
            dispatch(showToast({ color: "error", msg: "Question is required" }))
            return false
        }
        if (!queryDescription.trim()) {
            dispatch(showToast({ color: "error", msg: "Answer is required" }))
            return false
        }
        if (queryName.trim().length < 3) {
            dispatch(showToast({ color: "error", msg: "Question must be atleast 3 characters long" }))
            return false
        }
        if (queryDescription.trim().length < 2) {
            dispatch(showToast({ color: "error", msg: "Answer must be atleast 2 characters long" }))
            return false
        }
        return true
    }

    const handleAddQuery = async () => {
        try {
            if (!validate(queryName, queryDescription)) {
                return
            }
            setLoading(true);
            const response = await createFAQ({ title: queryName, content: queryDescription });
            if (response.status) {
                fetchFAQs();
                handleCloseDialog();
            }
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateQuery = async () => {
        try {
            if (!validate(queryName, queryDescription)) {
                return
            }
            setLoading(true);
            const response = await editFAQ(queries[editIndex]._id, { title: queryName, content: queryDescription });
            if (response.status) {
                await fetchFAQs()
                handleCloseDialog();
            }
        } catch (err) {
            console.log(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteQuery = () => {
        setOpenDeleteModal(true)
    };

    const ConfirmDeleteModalHandler = async () => {
        try {
            setLoading(true);
            await deleteFAQ(queries[currentIndex]._id);
            await fetchFAQs()
            setOpenDeleteModal(false)
        } catch (err) {
            console.log(err.message);
        } finally {
            setLoading(false);
            handleCloseMenu();
        }
    }

    const closeDeleteModalHandler = () => {
        setOpenDeleteModal(false)
    }

    const handleViewQuery = () => {
        setViewQuestion(queries[currentIndex].title);
        setViewDescription(queries[currentIndex].content);
        setOpenViewDialog(true);
        handleCloseMenu();
    };

    const handleOpenMenu = (event, index) => {
        setAnchorEl(event.currentTarget);
        setCurrentIndex(index);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage + 1);
    };

    return (<PageLayout
        title="FAQs"
        loading={loading}
        actions={
            <Button variant="contained" sx={{
                padding: {
                    xs: '6px', sm: '6px 16px', md: '6px 16px', lg: '6px 16px', xl: '6px 16px'
                }, backgroundColor: '#142C5C', color: '#fff', borderColor: '#0f2247', '&:hover': {
                    backgroundColor: '#0f2247',
                }, borderRadius: '6px', fontSize: {
                    xs: '12px', sm: '13px', md: '13px', lg: '14px', xl: '14px'
                }, height: {
                    xs: '35px', sm: '40px', md: '40px', lg: '40px', xl: '40px'
                }
            }} onClick={handleOpenDialog}>
                <Add /> Add FAQ
            </Button>
        }
    >
        <Box sx={{
            padding: '15px 15px 0 15px',
            borderRadius: '6px',
            boxShadow: 'rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px'
        }}>
            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table}>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell sx={{
                                fontSize: {
                                    xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px'
                                }, color: '#000', fontWeight: 600, padding: {
                                    xs: '10px 5px', sm: '15px 10px', md: '15px 10px', lg: '20px 15px', xl: '20px 15px'
                                }, backgroundColor: '#e0e0e0'
                            }}>S.No.</TableCell>
                            <TableCell sx={{
                                fontSize: {
                                    xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px'
                                }, color: '#000', fontWeight: 600, padding: {
                                    xs: '10px 5px', sm: '15px 10px', md: '15px 10px', lg: '20px 15px', xl: '20px 15px'
                                }, backgroundColor: '#e0e0e0'
                            }}>Question</TableCell>
                            <TableCell sx={{
                                fontSize: {
                                    xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px'
                                }, color: '#000', fontWeight: 600, padding: {
                                    xs: '10px 5px', sm: '15px 10px', md: '15px 10px', lg: '20px 15px', xl: '20px 15px'
                                }, backgroundColor: '#e0e0e0'
                            }}>Answer</TableCell>
                            <TableCell sx={{
                                fontSize: {
                                    xs: '14px', sm: '15px', md: '15px', lg: '16px', xl: '16px'
                                }, color: '#000', fontWeight: 600, padding: {
                                    xs: '10px 5px', sm: '15px 10px', md: '15px 10px', lg: '20px 15px', xl: '20px 15px'
                                }, backgroundColor: '#e0e0e0'
                            }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {queries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} sx={{
                                    textAlign: 'center',
                                    height: 'calc(100vh - 225px)',
                                    fontSize: {
                                        xs: '12px',
                                        sm: '13px',
                                        md: '14px',
                                        lg: '15px',
                                        xl: '15px'
                                    },
                                    color: '#333',
                                    padding: {
                                        xs: '10px 5px',
                                        sm: '10px',
                                        md: '10px',
                                        lg: '10px 15px',
                                        xl: '10px 15px'
                                    }
                                }}>
                                    No records found
                                </TableCell>
                            </TableRow>
                        ) : (
                            queries?.map((query, index) => (
                                <TableRow key={query._id}>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px', sm: '13px', md: '14px', lg: '15px', xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px', sm: '10px', md: '10px', lg: '10px 15px', xl: '10px 15px'
                                        }
                                    }}>
                                        {(page - 1) * limit + index + 1}
                                    </TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px', sm: '13px', md: '14px', lg: '15px', xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px', sm: '10px', md: '10px', lg: '10px 15px', xl: '10px 15px'
                                        }
                                    }}>
                                        <ExpandableText
                                            text={query?.title}
                                            maxLength={15}
                                            title="FAQ title"
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px', sm: '13px', md: '14px', lg: '15px', xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px', sm: '10px', md: '10px', lg: '10px 15px', xl: '10px 15px'
                                        }
                                    }}>
                                        <ExpandableText
                                            text={query?.content}
                                            maxLength={15}
                                            title="FAQ content"
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        fontSize: {
                                            xs: '12px', sm: '13px', md: '14px', lg: '15px', xl: '15px'
                                        }, color: '#333', padding: {
                                            xs: '10px 5px', sm: '10px', md: '10px', lg: '10px 15px', xl: '10px 15px'
                                        }
                                    }}>
                                        <IconButton sx={{
                                            '&:hover': {
                                                backgroundColor: '#142C5C', color: '#ffffff'
                                            }
                                        }} onClick={(e) => handleOpenMenu(e, (page - 1) * limit + index)}>
                                            <MoreVert />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>

                </Table>
            </TableContainer>
            <CommonPagination
                rowsPerPage={limit}
                page={page - 1}
                onPageChange={handlePageChange}
                count={totalFaq}
            />

        </Box>
        <Dialog sx={{
            zIndex: 9,
            '& .MuiDialog-container .MuiPaper-elevation': {
                borderRadius: '8px', width: '420px'
            }
        }} open={openDialog} onClose={handleCloseDialog}>
            <DialogTitle sx={{
                padding: '45 0 30px', textAlign: 'center', fontWeight: 700
            }}>{isEditMode ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
            <DialogContent>
                <Box mt={1}>
                    <CustomTextField
                        autoFocus
                        // margin="dense"
                        label="Question"
                        value={queryName}
                        onChange={(e) => setQueryName(e.target.value)}
                    />
                </Box>
                <Box mt={1}>
                    <CustomTextField
                        // margin="dense"
                        label="Answer"
                        fullWidth
                        multiline
                        rows={4}
                        value={queryDescription}
                        onChange={(e) => setQueryDescription(e.target.value)}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ paddingBottom: '15px' }}>
                <Button onClick={handleCloseDialog} sx={{
                    border: '1px solid #888', color: '#888', borderRadius: '6px', fontSize: '14px', '&:hover': {
                        backgroundColor: '#b9b9b9', color: '#222', borderColor: '#b9b9b9'
                    }
                }}>
                    Cancel
                </Button>
                <Button onClick={isEditMode ? handleUpdateQuery : handleAddQuery} sx={{
                    backgroundColor: '#142C5C', color: '#fff', borderColor: '#0f2247', '&:hover': {
                        backgroundColor: '#0f2247',
                    }, borderRadius: '6px', fontSize: {
                        xs: '12px', sm: '13px', md: '13px', lg: '14px', xl: '14px'
                    }, height: '40px'
                }}>
                    {isEditMode ? 'Update' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog sx={{
            '& .MuiDialog-container .MuiPaper-elevation': {
                width: '420px'
            },
            fontFamily: "Montserrat, sans-serif",
        }} open={openViewDialog} onClose={() => setOpenViewDialog(false)}>
            <DialogTitle sx={{
                padding: '30px 0 15px', textAlign: 'center', fontWeight: 700
            }}>View FAQ</DialogTitle>
            <DialogContent>
                <Box sx={{
                    padding: '10px 0',
                }}>
                    <Box sx={{
                        paddingBottom: '5px', display: 'flex', alignItems: 'center'
                    }}>
                        <Typography sx={{
                            fontSize: '16px', fontWeight: 600, marginRight: '10px',
                        }} variant="h6">Question:</Typography>
                        <Typography sx={{
                            fontSize: '16px', fontWeight: 600
                        }} variant="h6">{viewQuestion}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{
                            fontSize: '16px', fontWeight: 400, marginRight: '10px',
                        }} variant="body1">Answer:</Typography>
                        <Typography sx={{
                            fontSize: '16px', fontWeight: 400
                        }} variant="body1">
                            {viewDescription}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '0 24px 30px' }}>
                <Button
                    onClick={() => setOpenViewDialog(false)}
                    className={classes.cancelButton}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            sx={{
                '& .MuiPaper-elevation': {
                    borderRadius: '4px', '& .MuiList-padding': {
                        padding: 0
                    }
                }
            }}
        >

            <MenuItem sx={{
                padding: '5px 20px !important', borderBottom: '1px solid #e0e0e0', '&:hover': {
                    backgroundColor: '#e0e0e0 !important', color: '#000'
                }
            }} onClick={() => {
                setQueryName(queries[currentIndex].title);
                setQueryDescription(queries[currentIndex].content);
                setIsEditMode(true);
                setEditIndex(currentIndex);
                handleOpenDialog();
                handleCloseMenu();
            }}>

                Edit
            </MenuItem>
            <MenuItem sx={{
                padding: '5px 20px !important', borderBottom: '1px solid #e0e0e0', '&:hover': {
                    backgroundColor: '#e0e0e0 !important', color: '#000'
                }
            }} onClick={() => {
                handleDeleteQuery();
                handleCloseMenu();
            }}>

                Delete
            </MenuItem>
            <MenuItem sx={{
                padding: '5px 20px !important', '&:hover': {
                    backgroundColor: '#e0e0e0 !important', color: '#000'
                }
            }} onClick={handleViewQuery}>

                View
            </MenuItem>
        </Menu>
        <ToastContainer />
        {openDeleteModal && <ConfirmDeleteModal onAccept={ConfirmDeleteModalHandler} onReject={closeDeleteModalHandler} faq={true} />}
    </PageLayout>);
};

export default QueryManager;
















